// Экран подготовки кампании: черновики концепций, модерация,
// генерация мира, прогресс стадий. Вынесено из CampaignSetupPage.tsx
// без изменения поведения (Фаза 1). StageProgress/campaignStageValue/
// stageShortLabel переназначены сюда из Shell (разрыв цикла).

import { useState, type FormEvent } from "react";
import { api, type CampaignSetup } from "../api";
import { InvitePlayersPanel } from "./InvitePlayersPanel";
import {
  BlockHeader,
  MemberAvatar,
  ParagraphText,
  ProgressBar,
  Spinner,
} from "./shared";

function PreparationView({
  setup,
  worldName,
  concept,
  setWorldName,
  setConcept,
  submit,
  campaignId,
  action,
}: {
  setup: CampaignSetup;
  worldName: string;
  concept: string;
  setWorldName: (value: string) => void;
  setConcept: (value: string) => void;
  submit: (event: FormEvent) => Promise<boolean>;
  campaignId: string;
  action: (label: string, run: () => Promise<unknown>) => Promise<boolean>;
}) {
  const selection = setup.worldSelection;
  const [proposalModalOpen, setProposalModalOpen] = useState(false);
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const draftChecks = proposalDraftChecks(worldName, concept);
  const approvedProposals = selection.proposals.filter((proposal) => proposal.isEligibleForVoting);
  const readyUserIds = new Set(selection.readyUserIds ?? []);
  const skippedUserIds = new Set(selection.skippedUserIds ?? []);
  const canShowVoteNumbers = selection.phase === "WORLD_VOTING";
  const canSkip = Boolean(selection.canSkipProposal);
  const canForceStage = Boolean(
    selection.generationFailed ||
      selection.canOpenVoting ||
      selection.canFinalizeVoting ||
      selection.canForceFinalizeVoting ||
      ["WORLD_GENERATING", "RANDOM_ROLE_ASSIGNMENT", "PRIVATE_BRIEFING"].includes(selection.phase),
  );

  async function submitProposalAndMaybeClose(event: FormEvent) {
    const ok = await submit(event);
    if (ok) setProposalModalOpen(false);
  }

  async function skipProposal() {
    await action("Пропускаем предложение мира…", () => api.skipWorldProposal(campaignId));
  }

  async function forceOpenVoting() {
    const label = approvedProposals.length === 1
      ? "Выбираем единственную концепцию…"
      : "Открываем голосование…";
    await action(label, () => api.openWorldVoting(campaignId));
  }

  function renderProposalModal() {
    if (!proposalModalOpen) return null;
    return (
      <div className="cc-modal-backdrop cc-setup-modal-backdrop" role="dialog" aria-modal="true">
        <form className="cc-modal-card cc-setup-modal cc-proposal-form cc-setup-proposal-form" onSubmit={submitProposalAndMaybeClose}>
          <header className="cc-modal-header cc-setup-modal-header">
            <div>
              <small>Предложение мира</small>
              <h2>{selection.myProposalId ? "Доработать концепцию" : "Предложить мир"}</h2>
            </div>
            <button type="button" className="cc-modal-close cc-setup-modal-close" onClick={() => setProposalModalOpen(false)}>×</button>
          </header>
          <p className="cc-form-intro">
            Опишите основу будущей кампании. Старайтесь сразу указать место действия, власть, конфликт и кризис, с которого начинается игра.
          </p>
          <details className="cc-setup-hint-drawer">
            <summary>Что стоит описать</summary>
            <div className="cc-setup-prompt-grid">
              <div>
                <strong>Где происходит действие?</strong>
                <span>Город, колония, государство или иной центр управления.</span>
              </div>
              <div>
                <strong>Кто здесь живёт и управляет?</strong>
                <span>Общество, власть, влиятельные группы и противоречия.</span>
              </div>
              <div>
                <strong>Какой кризис начинается?</strong>
                <span>Проблема, из-за которой решения Совета действительно важны.</span>
              </div>
              <div>
                <strong>Что делает мир особенным?</strong>
                <span>Технология, магия, история, ограничение или атмосфера.</span>
              </div>
            </div>
          </details>
          <div className="cc-setup-field-grid">
            <label>
              Название мира <span className="cc-field-counter">{worldName.trim().length}/160</span>
              <input
                value={worldName}
                onChange={(event) => setWorldName(event.target.value)}
                maxLength={160}
                placeholder="Например: Архипелаг Последнего Света"
              />
            </label>
            <label className="cc-setup-field-span-2">
              Описание <span className="cc-field-counter">{concept.trim().length}/6000</span>
              <textarea
                value={concept}
                onChange={(event) => setConcept(event.target.value)}
                maxLength={6000}
                placeholder="Опишите мир минимум в 3–4 предложениях: устройство общества, исходный кризис, основные противоречия и уникальную особенность…"
              />
            </label>
          </div>
          <details className="cc-setup-check-drawer" open>
            <summary>Готовность черновика</summary>
            <DraftQualityChecklist worldName={worldName} concept={concept} />
          </details>
          <button className="primary" disabled={!draftChecks.every((check) => check.done)}>
            Проверить и сохранить
          </button>
        </form>
      </div>
    );
  }

  function renderInviteModal() {
    if (!inviteModalOpen) return null;
    const inviteCode = setup.campaign.inviteCode;
    const shareText = inviteCode
      ? `Присоединяйся к кампании City Chronicle «${setup.campaign.title}». Код входа: ${inviteCode}`
      : `Присоединяйся к кампании City Chronicle «${setup.campaign.title}».`;
    async function shareInviteCode() {
      if (navigator.share) {
        await navigator.share({ title: setup.campaign.title, text: shareText }).catch(() => undefined);
        return;
      }
      if (inviteCode) await navigator.clipboard.writeText(inviteCode).catch(() => undefined);
    }
    return (
      <div className="cc-modal-backdrop cc-setup-modal-backdrop" role="dialog" aria-modal="true">
        <div className="cc-modal-card cc-setup-modal cc-setup-invite-modal">
          <header className="cc-modal-header cc-setup-modal-header">
            <div>
              <small>Приглашение</small>
              <h2>Добавить игроков</h2>
            </div>
            <button type="button" className="cc-modal-close cc-setup-modal-close" onClick={() => setInviteModalOpen(false)}>×</button>
          </header>
          {inviteCode && (
            <section className="cc-setup-share-code-card">
              <span>Код кампании</span>
              <strong>{inviteCode}</strong>
              <button type="button" onClick={() => void shareInviteCode()}>
                Поделиться кодом
              </button>
            </section>
          )}
          <InvitePlayersPanel campaignId={campaignId} setup={setup} action={action} />
        </div>
      </div>
    );
  }

  return (
    <>
      <section className="cc-setup-overview-v2">
        <section className="cc-block cc-setup-card cc-setup-players-v2">
          <BlockHeader eyebrow="Участники" title="Игроки и приглашения" icon="users" />
          <div className="cc-setup-player-list-v2">
            {setup.members.map((member) => {
              const ready = readyUserIds.has(member.userId);
              const skipped = skippedUserIds.has(member.userId);
              return (
                <article className="cc-setup-player-row-v2" key={member.campaignMemberId}>
                  <MemberAvatar campaignId={campaignId} member={member} />
                  <div className="cc-setup-player-main-v2">
                    <strong>{member.displayName}{member.isCurrentUser ? " · вы" : ""}</strong>
                    <small>
                      {member.memberRole === "OWNER" ? "Организатор" : "Участник"}
                      {ready ? skipped ? " · пропустил предложение" : " · предложил мир" : " · ждёт решения"}
                    </small>
                  </div>
                  <span className={ready ? "ready" : "waiting"}>{ready ? "готов" : "ждёт"}</span>
                </article>
              );
            })}
            {setup.pendingInvitations.map((invitation) => (
              <article className="cc-setup-player-row-v2 pending" key={invitation.id}>
                <div className="cc-setup-pending-avatar" aria-hidden="true">?</div>
                <div className="cc-setup-player-main-v2">
                  <strong>{invitation.invitedUserDisplayName}</strong>
                  <small>{invitation.invitedUserUsername ? `@${invitation.invitedUserUsername}` : "ожидает приглашения"}</small>
                </div>
                <span className="waiting">приглашён</span>
              </article>
            ))}
          </div>
          {setup.canManage && (
            <button type="button" className="primary cc-setup-main-button-v2" onClick={() => setInviteModalOpen(true)}>
              Пригласить игрока
            </button>
          )}
        </section>

        <section className="cc-block cc-setup-card cc-setup-proposals-v2">
          <BlockHeader
            eyebrow="Миры"
            title={selection.phase === "WORLD_VOTING" ? "Голосование за концепцию" : "Предложенные миры"}
            icon="world"
          />
          {selection.proposals.length === 0 ? (
            <div className="empty-state cc-setup-empty-state">
              <h3>Пока нет предложенных миров</h3>
              <p>Нужна хотя бы одна концепция. Если все кроме последнего игрока пропустят, последний должен предложить мир.</p>
            </div>
          ) : (
            <div className="cc-card-list cc-setup-proposal-list-v2">
              {selection.proposals.map((proposal, index) => (
                <article className={`cc-proposal-card${proposal.isEligibleForVoting ? "" : " needs-revision"}`} key={proposal.id}>
                  <div className="cc-card-heading">
                    <div>
                      <div className="cc-proposal-title-row">
                        {canShowVoteNumbers && proposal.isEligibleForVoting && <strong className="cc-vote-index">{index + 1}</strong>}
                        <h3>{proposal.worldName}</h3>
                        <span className={`cc-moderation-badge ${proposal.isEligibleForVoting ? "approved" : "revision"}`}>
                          {proposal.isEligibleForVoting ? "Допущено" : "Нужно доработать"}
                        </span>
                      </div>
                      <span>{proposal.authorDisplayName}{proposal.isMine ? " · вы" : ""}</span>
                    </div>
                    {canShowVoteNumbers && proposal.isEligibleForVoting && <strong>{proposal.voteCount}</strong>}
                  </div>
                  <div className="cc-proposal-body">
                    <ParagraphText text={proposal.concept} />
                  </div>
                  {!proposal.isEligibleForVoting && (
                    <ProposalModerationFeedback
                      issues={proposal.moderationIssues}
                      suggestions={proposal.moderationSuggestions}
                    />
                  )}
                  {selection.phase === "WORLD_VOTING" && proposal.isEligibleForVoting && (
                    <button
                      className={selection.myVoteProposalId === proposal.id ? "primary" : "secondary"}
                      onClick={() =>
                        void action("Сохраняем ваш голос…", () => api.castWorldVote(campaignId, proposal.id))
                      }
                    >
                      {selection.myVoteProposalId === proposal.id ? "Проголосовано" : "Голосовать"}
                    </button>
                  )}
                </article>
              ))}
            </div>
          )}
          <div className="cc-setup-action-buttons-v2">
            {selection.canSubmitProposal && (
              <button type="button" className="primary" onClick={() => setProposalModalOpen(true)}>
                {selection.myProposalId ? "Доработать мир" : "Предложить мир"}
              </button>
            )}
            {selection.myProposalSkipped && (
              <span className="cc-muted cc-setup-inline-status">Вы пропустили предложение мира.</span>
            )}
            {selection.phase === "COLLECTING_WORLD_PROPOSALS" && (
              <button type="button" className="secondary" disabled={!canSkip} onClick={() => void skipProposal()}>
                Не предлагать мир
              </button>
            )}
            {!canSkip && selection.phase === "COLLECTING_WORLD_PROPOSALS" && approvedProposals.length === 0 && (
              <small className="cc-muted">Пропуск недоступен: должна остаться хотя бы одна концепция.</small>
            )}
          </div>
        </section>

        <section className="cc-block cc-setup-card cc-setup-stage-v2">
          <BlockHeader eyebrow="Этап" title={campaignStageValue(setup)} icon="check" />
          <StageProgress stage={setup.setupStage} />
          <div className="check-list cc-setup-progress-list">
            <SetupCheck done={setup.members.length >= 3} text="Собрано минимум 3 игрока" />
            <SetupCheck done={approvedProposals.length >= 1} text={`Есть хотя бы одна концепция мира (${approvedProposals.length})`} />
            <SetupCheck done={selection.phase !== "COLLECTING_WORLD_PROPOSALS"} text="Этап предложений завершён" />
          </div>
          {selection.generationFailed && (
            <section className="error-card cc-error cc-generation-failure" role="alert">
              <div>
                <strong>Создание мира завершилось ошибкой</strong>
                <p>{selection.generationFailureMessage ?? "Мир не создан. Запустите выбор повторно."}</p>
              </div>
            </section>
          )}
          {setup.canManage && selection.canOpenVoting && (
            <section className="cc-owner-force-card">
              <strong>Организатор может перейти дальше принудительно</strong>
              <p>
                Если предложена одна концепция, голосование будет пропущено. Если концепций несколько, откроется голосование.
              </p>
              <button type="button" onClick={() => void forceOpenVoting()}>
                {approvedProposals.length === 1 ? "Выбрать единственную концепцию" : "Запустить голосование"}
              </button>
            </section>
          )}
          {selection.canFinalizeVoting && (
            <button
              className="primary cc-setup-main-action-v2"
              onClick={() =>
                void action(
                  selection.generationFailed ? "Создаём мир заново…" : "Создаём мир, должности и личные досье…",
                  () => api.finalizeWorldVoting(campaignId),
                )
              }
            >
              {selection.generationFailed ? "Создать мир заново" : "Завершить выбор и создать мир"}
            </button>
          )}
          {selection.canForceFinalizeVoting && (
            <section className="cc-owner-force-card">
              <strong>Организатор может завершить голосование досрочно</strong>
              <p>Недостающие голоса не блокируют создание мира. Победитель будет выбран по уже отданным голосам.</p>
              <button
                type="button"
                onClick={() => void action("Принудительно завершаем голосование…", () => api.forceFinalizeWorldVoting(campaignId))}
              >
                Завершить голосование сейчас
              </button>
            </section>
          )}
          {canForceStage && ["WORLD_GENERATING", "RANDOM_ROLE_ASSIGNMENT", "PRIVATE_BRIEFING"].includes(selection.phase) && (
            <GenerationPanel phase={selection.phase} />
          )}
        </section>
      </section>
      {renderProposalModal()}
      {renderInviteModal()}
    </>
  );
}
function DraftQualityChecklist({
  worldName,
  concept,
}: {
  worldName: string;
  concept: string;
}) {
  return (
    <div className="cc-draft-checklist">
      <strong>Перед отправкой</strong>
      {proposalDraftChecks(worldName, concept).map((check) => (
        <div className={check.done ? "done" : ""} key={check.label}>
          <span>{check.done ? "✓" : "·"}</span>
          {check.label}
        </div>
      ))}
    </div>
  );
}
function ProposalModerationFeedback({
  issues,
  suggestions,
}: {
  issues: string[];
  suggestions: string[];
}) {
  return (
    <div className="cc-moderation-feedback">
      <div className="cc-moderation-heading">
        <strong>Предложение пока не опубликовано</strong>
        <span>Нужна доработка</span>
      </div>
      {issues.length > 0 && (
        <div className="cc-moderation-section">
          <b>Что мешает допуску</b>
          <ul>
            {issues.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      {suggestions.length > 0 && (
        <div className="cc-moderation-section">
          <b>Как исправить</b>
          <ul>
            {suggestions.map((item) => (
              <li key={item}>{item}</li>
            ))}
          </ul>
        </div>
      )}
      <p className="cc-moderation-note">
        Этот текст виден только вам. Измените описание выше и отправьте его на
        повторную проверку.
      </p>
    </div>
  );
}
function proposalDraftChecks(worldName: string, concept: string) {
  const words = concept.trim().match(/[\p{L}\p{N}][\p{L}\p{N}'’_-]*/gu) ?? [];
  const uniqueWords = new Set(
    words
      .filter((word) => word.length >= 3)
      .map((word) => word.toLocaleLowerCase("ru")),
  );
  const noLongRun = !/(.)\1{6,}/iu.test(`${worldName} ${concept}`);
  return [
    {
      label: "Понятное название минимум из 3 символов",
      done: worldName.trim().length >= 3,
    },
    {
      label: "Описание не короче 180 символов",
      done: concept.trim().length >= 180,
    },
    {
      label: "Не менее 24 слов и 12 разных содержательных слов",
      done: words.length >= 24 && uniqueWords.size >= 12,
    },
    { label: "Нет длинных повторов одинаковых символов", done: noLongRun },
  ];
}
function GenerationPanel({
  phase,
}: {
  phase: CampaignSetup["worldSelection"]["phase"];
}) {
  const value =
    phase === "WORLD_GENERATING"
      ? 38
      : phase === "RANDOM_ROLE_ASSIGNMENT"
        ? 72
        : 90;
  const label =
    phase === "WORLD_GENERATING"
      ? "Создаём устройство мира и пролог"
      : phase === "RANDOM_ROLE_ASSIGNMENT"
        ? "Распределяем должности и адаптируем персонажей"
        : "Формируем личные досье";
  return (
    <section className="cc-block cc-generation-panel">
      <div className="cc-generation-head">
        <Spinner />
        <div>
          <div className="eyebrow">Генерация продолжается</div>
          <h2>{label}</h2>
        </div>
        <strong>{value}%</strong>
      </div>
      <ProgressBar value={value} />
      <p>
        Экран обновится автоматически. Не нажимайте кнопку повторно и не
        закрывайте Mini App до завершения текущего шага.
      </p>
    </section>
  );
}
function SetupCheck({ done, text }: { done: boolean; text: string }) {
  return (
    <div className={done ? "check done" : "check"}>
      <span>{done ? "✓" : "·"}</span>
      {text}
    </div>
  );
}
function campaignStageValue(setup: CampaignSetup) {
  const cycle = setup.cycle.current;
  if (!cycle)
    return setup.rolesAssigned
      ? "Совет сформирован"
      : stageShortLabel(setup.setupStage);
  return (
    {
      PREPARING: "Подготовка",
      DAY: `${cycle.dayReadyCount}/${cycle.memberCount} завершили день`,
      DAY_CLOSING: "Последние встречи",
      AGENDA: `${cycle.agendaReadyCount}/${cycle.memberCount} готовы`,
      VOTING: "Голосование",
      RESOLVING: "Расчёт",
      NARRATING: "Хроника",
      PUBLISHED: "Опубликовано",
    } as const
  )[cycle.phase];
}
function StageProgress({ stage }: { stage: CampaignSetup["setupStage"] }) {
  const index = [
    "WAITING_FOR_PLAYERS",
    "COLLECTING_WORLD_PROPOSALS",
    "WORLD_VOTING",
    "WORLD_GENERATING",
    "RANDOM_ROLE_ASSIGNMENT",
    "PRIVATE_BRIEFING",
    "READY",
  ].indexOf(stage);
  return <ProgressBar value={((index + 1) / 7) * 100} />;
}
function stageShortLabel(stage: CampaignSetup["setupStage"]) {
  return {
    WAITING_FOR_PLAYERS: "Сбор игроков",
    COLLECTING_WORLD_PROPOSALS: "Идеи мира",
    WORLD_VOTING: "Голосование",
    WORLD_GENERATING: "Создание мира",
    RANDOM_ROLE_ASSIGNMENT: "Должности",
    PRIVATE_BRIEFING: "Досье",
    READY: "Готово",
  }[stage];
}

export {
  PreparationView,
  DraftQualityChecklist,
  ProposalModerationFeedback,
  proposalDraftChecks,
  GenerationPanel,
  SetupCheck,
  StageProgress,
  campaignStageValue,
  stageShortLabel,
};
